import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Select, SelectChangeEvent, FormControl, OutlinedInput } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { styled } from "@mui/material/styles";
import { Text } from "@fluentui/react-components";

import { useFilterStyles } from "../styles/filterStyles";
import SpreetailHeader from "../layout/SpreetailHeader";
import SpreetailLoader from "../common/SpreetailLoader";
import DateRangeFilter from "../filters/controls/DateRangeFilter";
import BrandFilter from "../filters/controls/BrandFilter";
import SkusFilter from "../filters/controls/SkusFilter";
import KeywordFilter from "../filters/controls/KeywordFilter";
import CampaignTypeFilter from "../filters/controls/CampaignTypeFilter";
import { useInterdependentAdvertisingFilters } from "../../hooks/useInterdependentAdvertisingFilters";
import { updateCopiedAdvertisingSheet } from "../../advertisingRowdata";
import { updateAdvertisingFilterStateForSheet, migrateAdvertisingFilterState } from "../../../shared/filterStateManager";
import { fetchFilteredAdvertisingData } from "../../../shared/mockData";
import { isRawDataSheet } from "../../rowdata";
import { CUSTOM_PROPERTIES } from "../../../shared/constants";

interface SheetOption {
  name: string;
  isActive: boolean;
}

const StyledSelect = styled(Select)({
  backgroundColor: '#FFFFFF99',
  borderRadius: '12px',
  minHeight: '48px',
  fontSize: '14px',
  fontFamily: 'inherit',
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1.5px solid #E5E7EB',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: '1.5px solid #E5E7EB',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: '2px solid #3B82F6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
  },
  '& .MuiSelect-select': {
    padding: '12px 14px',
    paddingRight: '40px',
    minHeight: '24px',
  },
});

const PreserveComputationRoute: React.FC = () => {
  const filterStyles = useFilterStyles();

  const [currentSheetName, setCurrentSheetName] = React.useState<string | undefined>(undefined);
  const [sheets, setSheets] = useState<SheetOption[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>("");
  const sheetTrackingRef = React.useRef<{ id: string; name: string } | undefined>(undefined);
  const [loadingSheets, setLoadingSheets] = useState<boolean>(true);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const[errorMessage, setErrorMessage] = useState<string | null>(null);


  useEffect(() => {
    const migrateFiltersOnRename = async (oldName: string, newName: string) => {
      try {
        await migrateAdvertisingFilterState(oldName, newName);
      } catch (error) {
        console.error('Error migrating filters on rename:', error);
      }
    };
    
    const getCurrentSheetName = async () => {
      try {
        await Excel.run(async (context) => {
          const activeSheet = context.workbook.worksheets.getActiveWorksheet();
          activeSheet.load("name, id");
          await context.sync();
          const newSheetName = activeSheet.name;
          const newSheetId = activeSheet.id;
          
          // Check if sheet was renamed (same ID, different name)
          if (
            sheetTrackingRef.current &&
            sheetTrackingRef.current.id === newSheetId &&
            sheetTrackingRef.current.name !== newSheetName
          ) {
            // Check if the old sheet had advertising filters by checking custom properties
            const oldSheet = context.workbook.worksheets.getItemOrNullObject(sheetTrackingRef.current.name);
            oldSheet.load("isNullObject");
            await context.sync();
            
            if (!oldSheet.isNullObject) {
              const prop = oldSheet.customProperties.getItemOrNullObject(CUSTOM_PROPERTIES.ADVERTISING_FILTERS);
              prop.load(["isNullObject"]);
              await context.sync();
              
              if (!prop.isNullObject) {
                // Old sheet had advertising filters - migrate them
                console.log(
                  `📝 Sheet "${sheetTrackingRef.current.name}" (ID: ${newSheetId}) renamed to "${newSheetName}" - migrating filters`
                );
                await migrateFiltersOnRename(sheetTrackingRef.current.name, newSheetName);
              }
            }
          }
          
          sheetTrackingRef.current = { id: newSheetId, name: newSheetName };
          setCurrentSheetName(newSheetName);
        });
      } catch (error) {
        console.error('Error getting current sheet name:', error);
      }
    };
    
    getCurrentSheetName();
    
    let worksheetActivationHandler: any = null;
    let worksheetNameChangeHandler: any = null;
    
    Excel.run(async (context) => {
      worksheetActivationHandler = context.workbook.worksheets.onActivated.add(async () => {
        try {
          await getCurrentSheetName();
        } catch (error) {
          console.error('Error updating sheet name on activation:', error);
        }
      });
      
      worksheetNameChangeHandler = context.workbook.worksheets.onNameChanged.add(async () => {
 
        try {
          await getCurrentSheetName();
        } catch (error) {
          console.error('Error checking sheet name after change:', error);
        }
      });
      
      await context.sync();
    });
    
    return () => {
      if (worksheetActivationHandler || worksheetNameChangeHandler) {
        Excel.run(async (context) => {
          try {
            if (worksheetActivationHandler) {
              context.workbook.worksheets.onActivated.remove(worksheetActivationHandler);
            }
            if (worksheetNameChangeHandler) {
              context.workbook.worksheets.onNameChanged.remove(worksheetNameChangeHandler);
            }
            await context.sync();
          } catch (error) {
            // Ignore cleanup errors
          }
        });
      }
    };
  }, []);


  const {
    startDate,
    endDate,
    selected,
    options: interOptions,
    isLoading: optionsLoading,
    activationOrder,
    setStartDate,
    setEndDate,
    setBrand: setBrandInter,
    setSkus: setSkusInter,
    setKeyword: setKeywordInter,
    setCampaign: setCampaignInter,
    activate,
    clear: clearFilters,
    clearFromLevel,
  } = useInterdependentAdvertisingFilters();
  
  // Load filters for selected sheet when it changes (but don't auto-save changes back)
  React.useEffect(() => {
    if (!selectedSheet) return;
    
    const loadFiltersForSheet = async () => {
      try {
        const { hydrateAdvertisingFiltersForSheet } = await import("../../../shared/filterStateManager");
        const filters = await hydrateAdvertisingFiltersForSheet(selectedSheet);
        
        // Only update if we have actual filters (not empty defaults)
        if (filters.startDate || filters.endDate || filters.brand.length > 0 || filters.skus.length > 0 || filters.keyword.length > 0 || filters.campaign.length > 0) {
          if (filters.startDate) setStartDate(filters.startDate);
          if (filters.endDate) setEndDate(filters.endDate);
          if (filters.brand.length > 0) setBrandInter(filters.brand);
          if (filters.skus.length > 0) setSkusInter(filters.skus);
          if (filters.keyword.length > 0) setKeywordInter(filters.keyword);
          if (filters.campaign.length > 0) setCampaignInter(filters.campaign);
        } else {
          // Clear filters if sheet has no filters
          clearFilters();
        }
      } catch (error) {
        console.error("Error loading filters for selected sheet:", error);
      }
    };
    
    loadFiltersForSheet();
  }, [selectedSheet]);

  const brand = selected.brand;
  const skus = selected.skus;
  const keyword = selected.keyword;
  const campaign = selected.campaign;

  const hasActiveFilters = Boolean(
    startDate ||
    endDate ||
    brand.length ||
    skus.length ||
    keyword.length ||
    campaign.length
  );




  const getFilterLevel = (filterName: 'brand' | 'skus' | 'keyword' | 'campaign') => {
    const index = activationOrder.indexOf(filterName);
    if (index < 0) return undefined;
    const hasValues = (() => {
      switch (filterName) {
        case 'brand': return selected.brand.length > 0;
        case 'skus': return selected.skus.length > 0;
        case 'keyword': return selected.keyword.length > 0;
        case 'campaign': return selected.campaign.length > 0;
        default: return false;
      }
    })();
    const level = hasValues ? index + 1 : undefined;
    return level;
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await Excel.run(async (context) => {
          const worksheets = context.workbook.worksheets;
          worksheets.load("items/name");
          const active = context.workbook.worksheets.getActiveWorksheet();
          active.load("name");
          await context.sync();

          const activeName = active.name;
          const options: SheetOption[] = worksheets.items
            .filter(ws => isRawDataSheet(ws.name) && ws.name.includes("Advertising"))
            .map(ws => ({ name: ws.name, isActive: ws.name === activeName }));

          if (!mounted) return;
          setSheets(options);
          const defaultName = options.find(o => o.isActive)?.name || options[0]?.name || "";
          setSelectedSheet(defaultName);
        });
      } catch (_) {
        // ignore
      } finally {
        if (mounted) setLoadingSheets(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleClearFilters = async () => {
    await clearFilters();
    setErrorMessage(null);
    setShowSuccess(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
  };

  const handleApply = async () => {
    if (!selectedSheet) return;
    setIsGenerating(true);
    setErrorMessage(null);
    setShowSuccess(false);

    try {
      const filterParams = {
        startDate: startDate?.toISOString().split('T')[0],
        endDate: endDate?.toISOString().split('T')[0],
        brand,
        skus,
        channel: 'all',
        keyword: keyword.length > 0 ? keyword : [],
        campaign: campaign.length > 0 ? campaign : []
      };

      // Build filters for API call
      const filtersForApi: any = {};
      if (filterParams.startDate && filterParams.endDate) {
        filtersForApi.startDate = filterParams.startDate;
        filtersForApi.endDate = filterParams.endDate;
      }
      if (filterParams.brand && filterParams.brand.length > 0) filtersForApi.brands = filterParams.brand;
      if (filterParams.skus && filterParams.skus.length > 0) filtersForApi.asins = filterParams.skus;
      if (filterParams.channel && filterParams.channel !== 'all') filtersForApi.channels = [filterParams.channel];
      if (filterParams.keyword && filterParams.keyword.length > 0) filtersForApi.keyword_contains = filterParams.keyword;
      if (filterParams.campaign && filterParams.campaign.length > 0) filtersForApi.campaign_name = filterParams.campaign;

      // Build applied summary
      const parts: string[] = [];
      if (filterParams.keyword && filterParams.keyword.length > 0) parts.push(`Keyword: ${Array.isArray(filterParams.keyword) ? filterParams.keyword.join(', ') : filterParams.keyword}`);
      if (filterParams.campaign && filterParams.campaign.length > 0) parts.push(`Campaign: ${filterParams.campaign.join(', ')}`);
      if (filterParams.brand && filterParams.brand.length > 0) parts.push(`Brand: ${filterParams.brand.join(', ')}`);
      if (filterParams.skus && filterParams.skus.length > 0) parts.push(`ASIN/SKU: ${filterParams.skus.join(', ')}`);
      if (filterParams.startDate && filterParams.endDate) parts.push(`Dates: ${filterParams.startDate} - ${filterParams.endDate}`);
      const appliedSummary = parts.length > 0 ? `Filters: ${parts.join(' • ')}` : '';

      // Fetch filtered data BEFORE copying the sheet
      console.log('🔍 Fetching filtered data before sheet copy...');
      const resp = await fetchFilteredAdvertisingData(filtersForApi);
       
        if(resp?.row_count && resp?.row_count > 50000) {
          setErrorMessage(`Filtering has returned more than 50,000 rows (${resp?.row_count}). Please refine your filters.`);
          return;
    }

      const columns: string[] = resp?.columns || [];
      const data: any[][] = resp?.data || [];
      console.log('✅ Filtered data fetched, ready to render sheet');

      await Excel.run(async (context) => {
        const worksheets = context.workbook.worksheets;
        worksheets.load("items/name");
        await context.sync();

        const source = worksheets.items.find(w => w.name === selectedSheet);
        if (!source) return;

        // Now copy the sheet - data is already fetched
        const copied = source.copy(Excel.WorksheetPositionType.after, source);
        await context.sync();

        copied.load("name");
        await context.sync();
        const copiedSheetName = copied.name;
        
        console.log('💾 Saving filters for sheet:', copiedSheetName);
        await updateAdvertisingFilterStateForSheet(copiedSheetName, {
          startDate: startDate || null,
          endDate: endDate || null,
          brand: brand || [],
          skus: skus || [],
          channel: 'all',
          keyword: keyword || [],
          campaign: campaign || [],
        });
        
        console.log(`✅ Filters saved for "${copiedSheetName}":`, {
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          brand,
          skus,
          keyword,
          campaign
        });

        // Pass pre-fetched data to updateCopiedAdvertisingSheet
        await updateCopiedAdvertisingSheet(context, copied, {
          columns,
          data,
          appliedSummary
        });
        await context.sync();
      });

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (e) {
      console.error("Error applying preserve computation:", e);
      const msg=e?.message ||"";
      if(
        msg.includes("number of rows or columns") ||
        msg.includes("doesn't match the size") ||
        msg.includes("No data available")
      ) {
        setErrorMessage("No data available for the selected filters. Please try adjusting your date range or filter criteria.");
      } else {
        setErrorMessage(msg || "An unknown error occurred. Please try again.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  if (isGenerating) {
    return (
      <div className={filterStyles.filterContainer} style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
        <SpreetailHeader />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
          <SpreetailLoader
            title="Applying Preserve Computation"
            subtitle="Please wait while we render your sheet"
            size="large"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={filterStyles.filterContainer} style={{ height: "100%", minHeight: 0 }}>
      <SpreetailHeader />
      <div
        className={filterStyles.filterContent}
        style={{ overflowY: "auto", flex: 1, minHeight: 0, padding: "0 16px" }}
      >
        <div className={filterStyles.filterHeader}>
          <Text size={400} weight="semibold" style={{ color: "#0F172A" }}>
            Preserve Computation
          </Text>
          <Text size={200} style={{ color: "#667085", opacity: 0.9, fontSize: "12px" }}>
            Choose  a Raw Data sheet and optional filters, then render
          </Text>

          {errorMessage && (
            <div
              style={{
              marginTop: "8px",
              padding: "12px 14px",
              borderRadius: "10px",
              border: "1px solid rgba(251,146,60,0.35)",
              background: "rgba(251,146,60,0.08)",
              display: "flex",
              gap: "10px",
              alignItems: "flex-start",
              boxShadow: "0 6px 18px rgba(15,23,42,0.06)",
              backdropFilter: "blur(6px)",
            }}
            >
                    <div
        style={{
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          background: "rgba(249,115,22,0.9)",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: "12px",
          flexShrink: 0,
          marginTop: "2px",
        }}
      >
        !
      </div>
      <p
        style={{
          margin: 0,
          fontSize: "13px",
          color: "#9A3412",
          lineHeight: 1.5,
        }}
      >
        {errorMessage}
      </p>
    </div>
  )}
        </div>

        {/* Top action buttons */}
        <div
          style={{
            padding: "0 0 16px 0",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <button
            onClick={handleApply}
            disabled={isGenerating || loadingSheets || !selectedSheet}
            style={{
              backgroundColor: "rgba(16,185,129,0.12)",
              border: "1.5px solid rgba(16,185,129,0.35)",
              borderRadius: "8px",
              color: "#065F46",
              fontSize: "11px",
              fontWeight: 500,
              padding: "4px 8px",
              textTransform: "none",
              height: "28px",
              lineHeight: 1.2,
              minWidth: "auto",
              cursor: isGenerating ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              opacity: isGenerating ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(16,185,129,0.18)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(16,185,129,0.12)";
            }}
          >
            {isGenerating ? "Rendering…" : "Render Sheet"}
          </button>

          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              style={{
                backgroundColor: "rgba(239,68,68,0.10)",
                border: "1.5px solid rgba(239,68,68,0.35)",
                borderRadius: "8px",
                color: "#7F1D1D",
                fontSize: "11px",
                fontWeight: 500,
                padding: "4px 8px",
                textTransform: "none",
                height: "28px",
                lineHeight: 1.2,
                minWidth: "auto",
                cursor: "pointer",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.16)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.10)";
              }}
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Sheet Selection - moved above Date Range */}
        <Box className={filterStyles.inputGroup}>
          <label className={filterStyles.labelText}>Select Sheet to Preserve</label>
          <FormControl fullWidth>
            <StyledSelect
              value={selectedSheet}
              onChange={(e: SelectChangeEvent<string>) => setSelectedSheet(e.target.value)}
              displayEmpty
              fullWidth
              disabled={loadingSheets}
              input={<OutlinedInput />}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{
                PaperProps: { style: { maxHeight: 300 } },
              }}
            >
              {sheets.map((s) => (
                <MenuItem key={s.name} value={s.name}>
                  {s.name}
                  {s.isActive ? " (Active)" : ""}
                </MenuItem>
              ))}
              {!loadingSheets && sheets.length === 0 && (
                <MenuItem value="" disabled>
                  No Advertising Raw Data sheets found
                </MenuItem>
              )}
            </StyledSelect>
          </FormControl>
        </Box>

        {/* Date Range */}
        <Box className={filterStyles.inputGroup}>
          <label className={filterStyles.labelText}>Date Range</label>
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onChangeStart={setStartDate}
            onChangeEnd={setEndDate}
          />
        </Box>

        {/* Brand */}
        <Box className={filterStyles.inputGroup}>
          <BrandFilter
            value={selected.brand}
            onChange={setBrandInter}
            options={interOptions.brands}
            isLoading={optionsLoading}
            onActivate={() => activate("brand")}
            level={getFilterLevel("brand")}
            onClearLevel={clearFromLevel}
          />
        </Box>

        {/* SKUs */}
        <Box className={filterStyles.inputGroup}>
          <SkusFilter
            value={selected.skus}
            onChange={setSkusInter}
            options={interOptions.asins}
            isLoading={optionsLoading}
            onActivate={() => activate("skus")}
            level={getFilterLevel("skus")}
            onClearLevel={clearFromLevel}
          />
        </Box>

        {/* Keyword */}
        <Box className={filterStyles.inputGroup}>
          <KeywordFilter
            value={selected.keyword}
            onChange={setKeywordInter}
            options={interOptions.keywords}
            isLoading={optionsLoading}
            onActivate={() => activate("keyword")}
            level={getFilterLevel("keyword")}
            onClearLevel={clearFromLevel}
          />
        </Box>

        {/* Campaign Type */}
        <Box className={filterStyles.inputGroup}>
          <CampaignTypeFilter
            value={selected.campaign}
            onChange={setCampaignInter}
            options={interOptions.campaigns}
            isLoading={optionsLoading}
            onActivate={() => activate("campaign")}
            level={getFilterLevel("campaign")}
            onClearLevel={clearFromLevel}
          />
        </Box>
      </div>
    </div>
  );
};

export default PreserveComputationRoute;


